import React from 'react';

import { REDUCED_MOTION } from '../../../../theme/tokens';

export type StripScroll = {
  /** Attach to the scrolling strip element. */
  ref: React.RefObject<HTMLDivElement>;
  /** The strip can scroll at all (its cards overflow it). Both arrows render iff this is true. */
  canScroll: boolean;
  /** Scrolled to the very start: the previous arrow is disabled (nothing before the first card). */
  atStart: boolean;
  /** Scrolled to the very end: the next arrow is disabled (nothing after the last card). */
  atEnd: boolean;
  scrollLeft: () => void;
  scrollRight: () => void;
};

/** The media query as `matchMedia` wants it (the token carries the `@media` prefix for CSS). */
const REDUCED_MOTION_QUERY = REDUCED_MOTION.replace(/^@media\s*/, '');

/** Instant under `prefers-reduced-motion: reduce`, smooth otherwise (the strip's own CSS
 *  `scroll-behavior` flips the same way, so `auto` really is a jump there). */
const scrollBehavior = (): ScrollBehavior =>
  typeof window !== 'undefined' && window.matchMedia?.(REDUCED_MOTION_QUERY).matches ? 'auto' : 'smooth';

/** 1px tolerance absorbs sub-pixel rounding at the extremes. */
const EDGE_PX = 1;

/**
 * Track a horizontal strip's scroll position for its nav arrows, and page it ONE CARD at a time.
 *
 *  - `canScroll` is true when the cards overflow the strip; when everything fits no arrows render.
 *  - `atStart` / `atEnd` say which arrow is at its end. The arrows stay rendered either way (the
 *    header must not change width as you scroll); the consumer disables the one at its end.
 *  - `scrollLeft` / `scrollRight` move by exactly one card: the first card's width plus the strip's
 *    column gap, MEASURED from the DOM at press time rather than assumed, because the card width is
 *    responsive and a constant stride drifts out of agreement with the layout the moment the
 *    breakpoint changes. With `scroll-snap-type: x mandatory` on the strip the card then lands flush
 *    with the strip's left edge.
 *  - Paging STOPS at either end (the user, 2026-10-21): previous at the first card and next at the
 *    last do nothing, their arrows disabled, so the owner can tell they have seen every card. No
 *    wrap and no infinite carousel (no cloned nodes, which would also fight scroll-snap, put
 *    duplicate buttons in the tab order and make the count badge disagree with the DOM).
 *
 * Re-measures on scroll, on resize (ResizeObserver), and whenever `itemCount` changes (adding or
 * removing cards shifts the scrollable width without resizing the strip element), before paint so
 * the arrows and the fade mask are right on the first frame.
 *
 * The strip always opens on its first card (the user, 2026-10-21): whenever the cards change (their
 * count, or which one leads: `leadKey`), it is brought back to the start, at once, before paint.
 * Scroll snapping would otherwise keep the card it was snapped to in view, so a card arriving at the
 * front (Home's cards come in as their reads land) left the strip on the last one.
 */
export const useStripScroll = (itemCount: number, leadKey?: string): StripScroll => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = React.useState(false);
  const [atStart, setAtStart] = React.useState(true);
  const [atEnd, setAtEnd] = React.useState(true);

  const measure = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScroll(scrollWidth > clientWidth + EDGE_PX);
    setAtStart(scrollLeft <= EDGE_PX);
    setAtEnd(scrollLeft + clientWidth >= scrollWidth - EDGE_PX);
  }, []);

  // Attached when the strip is in the DOM, not only on mount: the consumer renders nothing while it
  // has no cards (Home's cold load), so on mount there is no element yet, and the listeners must
  // attach once the first cards bring the strip in.
  const hasItems = itemCount > 0;
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      ro?.disconnect();
    };
  }, [measure, hasItems]);

  // Measure before paint (initial + whenever the card count changes): the fade mask + arrows are
  // then correct on the first paint rather than flashing in a frame later. A layout effect also
  // catches a card-count change, which shifts the scrollable width without resizing the strip.
  React.useLayoutEffect(() => {
    // Back to the first card, instantly (the strip's CSS glides otherwise), then measured there.
    ref.current?.scrollTo?.({ left: 0, behavior: 'instant' });
    measure();
  }, [measure, itemCount, leadKey]);

  /** One card's stride: the first card's width plus the strip's column gap, read live. */
  const stride = (el: HTMLElement): number => {
    const first = el.firstElementChild as HTMLElement | null;
    if (!first) return 0;
    const styles = getComputedStyle(el);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return first.offsetWidth + gap;
  };

  const page = React.useCallback((dir: 1 | -1) => {
    const el = ref.current;
    if (!el) return;
    const step = stride(el);
    if (step === 0) return;
    const max = el.scrollWidth - el.clientWidth;
    // Decided from the live position, not the last render's state, so a press during a smooth
    // scroll still does the right thing: nothing past either end.
    if (dir === 1 && el.scrollLeft >= max - EDGE_PX) return;
    if (dir === -1 && el.scrollLeft <= EDGE_PX) return;
    el.scrollBy({ left: dir * step, behavior: scrollBehavior() });
  }, []);

  const scrollLeft = React.useCallback(() => page(-1), [page]);
  const scrollRight = React.useCallback(() => page(1), [page]);

  return { ref, canScroll, atStart, atEnd, scrollLeft, scrollRight };
};
